/**
 * Analysis Tab Component for Meeting Detail
 * Displays AI-generated analysis: summary, key points, decisions, action items
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Brain, Lightbulb, CheckCircle, AlertTriangle, Target,
  Loader2, RefreshCw, ListChecks, Users, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AnalysisTab = ({
  meeting,
  token,
  analysis,
  onAnalysisUpdate
}) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('summary');

  const runAnalysis = async () => {
    try {
      setAnalyzing(true);
      const response = await axios.post(
        `${API}/api/meeting-intelligence/meetings/${meeting.id}/analyze?token=${token}`
      );
      toast.success('AI analysis complete');
      if (onAnalysisUpdate) onAnalysisUpdate(response.data.analysis);
    } catch (err) {
      console.error('Analysis failed:', err);
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  // Normalize analysis data - handle both flat and raw_analysis nested structures
  const normalizedAnalysis = analysis ? {
    summary: analysis.summary || analysis.raw_analysis?.summary || '',
    key_points: analysis.key_points || analysis.raw_analysis?.key_points || [],
    decisions: analysis.decisions || analysis.raw_analysis?.decisions || [],
    action_items: analysis.action_items || analysis.raw_analysis?.action_items || [],
    topics: analysis.topics || analysis.raw_analysis?.next_meeting_topics || [],
    risks: analysis.risks || analysis.raw_analysis?.risks_issues?.map(r => 
      typeof r === 'string' ? r : r.description
    ) || []
  } : null;

  const hasAnalysis = normalizedAnalysis && (normalizedAnalysis.summary || normalizedAnalysis.key_points?.length > 0);

  return (
    <Card data-testid="analysis-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Analysis
            </CardTitle>
            <CardDescription>
              {hasAnalysis ? 'AI-generated insights from this meeting' : 'Generate AI analysis'}
            </CardDescription>
          </div>
          
          <Button 
            variant={hasAnalysis ? "outline" : "default"}
            onClick={runAnalysis}
            disabled={analyzing || !meeting.transcript?.text}
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : hasAnalysis ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-analyze
              </>
            ) : (
              <>
                <Brain className="h-4 w-4 mr-2" />
                Run Analysis
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {!hasAnalysis ? (
          <div className="text-center py-8 text-slate-500">
            <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No analysis available</p>
            <p className="text-sm">
              {meeting.transcript?.text 
                ? 'Click "Run Analysis" to generate AI insights'
                : 'Generate a transcript first'}
            </p>
          </div>
        ) : (
          <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="summary" className="text-xs sm:text-sm">
                <Lightbulb className="h-4 w-4 mr-1 hidden sm:block" />
                Summary
              </TabsTrigger>
              <TabsTrigger value="keypoints" className="text-xs sm:text-sm">
                <ListChecks className="h-4 w-4 mr-1 hidden sm:block" />
                Key Points
              </TabsTrigger>
              <TabsTrigger value="decisions" className="text-xs sm:text-sm">
                <Target className="h-4 w-4 mr-1 hidden sm:block" />
                Decisions
              </TabsTrigger>
              <TabsTrigger value="actions" className="text-xs sm:text-sm">
                <CheckCircle className="h-4 w-4 mr-1 hidden sm:block" />
                Actions
              </TabsTrigger>
            </TabsList>
            
            {/* Summary Tab */}
            <TabsContent value="summary" className="mt-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Executive Summary
                </h4>
                <p className="text-sm whitespace-pre-wrap">
                  {normalizedAnalysis.summary || 'No summary available'}
                </p>
              </div>
              
              {normalizedAnalysis.topics?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Topics Discussed</h4>
                  <div className="flex flex-wrap gap-2">
                    {normalizedAnalysis.topics.map((topic, idx) => (
                      <Badge key={idx} variant="secondary">{topic}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Key Points Tab */}
            <TabsContent value="keypoints" className="mt-4">
              {normalizedAnalysis.key_points?.length > 0 ? (
                <ul className="space-y-2">
                  {normalizedAnalysis.key_points.map((point, idx) => (
                    <li 
                      key={idx}
                      className="flex items-start gap-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                    >
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-slate-500 py-4">No key points extracted</p>
              )}
            </TabsContent>
            
            {/* Decisions Tab */}
            <TabsContent value="decisions" className="mt-4">
              {normalizedAnalysis.decisions?.length > 0 ? (
                <ul className="space-y-3">
                  {normalizedAnalysis.decisions.map((decision, idx) => (
                    <li 
                      key={idx}
                      className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border-l-4 border-blue-500"
                    >
                      <div className="flex items-start justify-between">
                        <span className="text-sm font-medium">{decision.description || decision.decision || decision}</span>
                        {decision.impact && (
                          <Badge variant={
                            decision.impact === 'high' ? 'destructive' : 
                            decision.impact === 'medium' ? 'warning' : 'secondary'
                          }>
                            {decision.impact} impact
                          </Badge>
                        )}
                      </div>
                      {decision.rationale && (
                        <p className="text-xs text-slate-500 mt-1">{decision.rationale}</p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-slate-500 py-4">No decisions recorded</p>
              )}
            </TabsContent>
            
            {/* Action Items Tab */}
            <TabsContent value="actions" className="mt-4">
              {normalizedAnalysis.action_items?.length > 0 ? (
                <ul className="space-y-3">
                  {normalizedAnalysis.action_items.map((action, idx) => (
                    <li 
                      key={idx}
                      className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{action.task || action}</p>
                          
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {action.owner && (
                              <Badge variant="outline" className="text-xs">
                                <Users className="h-3 w-3 mr-1" />
                                {action.owner}
                              </Badge>
                            )}
                            {action.due_date && action.due_date !== 'TBD' && (
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="h-3 w-3 mr-1" />
                                {action.due_date}
                              </Badge>
                            )}
                            {action.priority && (
                              <Badge variant={
                                action.priority === 'high' || action.priority === 'High' ? 'destructive' :
                                action.priority === 'medium' || action.priority === 'Medium' ? 'warning' : 'secondary'
                              } className="text-xs">
                                {action.priority}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-slate-500 py-4">No action items extracted</p>
              )}
            </TabsContent>
          </Tabs>
        )}
        
        {/* Risks Section */}
        {normalizedAnalysis?.risks?.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Risks & Concerns
            </h4>
            <ul className="space-y-2">
              {normalizedAnalysis.risks.map((risk, idx) => (
                <li 
                  key={idx}
                  className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-sm"
                >
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalysisTab;
